import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// DigitalPass components
import MDBox from "mui_components/MDBox";
import MDButton from "mui_components/MDButton";
import MDInput from "mui_components/MDInput";
import MDTypography from "mui_components/MDTypography";
import DashboardNavbar from "layout/Navbars/DashboardNavbar";
// DigitalPass context
import { useDigitalPassController, setLayout } from "context";
import announcementConfig, { AnnouncementModal } from "config/announcement";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from "@mui/material";
import useAxios from "customHooks/useAxios";
import {
  PASSWORD_RULE_TEXT,
  validateConfirmPassword,
  validatePassword,
} from "utils/passwordValidation";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { requestOTPAPI, verifyOTPAPI } from "components/Common/otpApis";

function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const [controller, dispatch] = useDigitalPassController();
  const { miniSidenav, session } = controller;
  const { pathname } = useLocation();
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [forcePasswordModalOpen, setForcePasswordModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [pendingPasswordPrompt, setPendingPasswordPrompt] = useState(false);
  const [pendingContactPrompt, setPendingContactPrompt] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [contactNo, setContactNo] = useState(controller?.userDetails?.contactNo || "");
  const [contactOtp, setContactOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [verifyingContactOtp, setVerifyingContactOtp] = useState(false);
  const [logoutNoticeOpen, setLogoutNoticeOpen] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(5);
  const { post } = useAxios();

  useEffect(() => {
    if (!session || session.length < 5) {
      navigate("/auth/signin");
    }
    setLayout(dispatch, "dashboard");
  }, [pathname, dispatch, session, navigate]);

  useEffect(() => {
    const shouldShowAnnouncement =
      announcementConfig?.active === 1 &&
      sessionStorage.getItem("showPostLoginAnnouncement") === "1";
    const shouldShowPasswordModal = sessionStorage.getItem("forcePasswordChange") === "1";
    const shouldShowContactModal =
      !controller?.userDetails?.isContactVerified || !controller?.userDetails?.contactNo;

    if (shouldShowAnnouncement) {
      setAnnouncementOpen(true);
      sessionStorage.removeItem("showPostLoginAnnouncement");
      setPendingPasswordPrompt(shouldShowPasswordModal);
      setPendingContactPrompt(!shouldShowPasswordModal && shouldShowContactModal);
      setForcePasswordModalOpen(false);
      setContactModalOpen(false);
    } else if (shouldShowPasswordModal) {
      setForcePasswordModalOpen(true);
      setContactModalOpen(false);
    } else if (shouldShowContactModal) {
      setContactModalOpen(true);
    }
  }, [pathname, controller?.userDetails?.isContactVerified, controller?.userDetails?.contactNo]);

  const handleAnnouncementClose = () => {
    setAnnouncementOpen(false);
    if (pendingPasswordPrompt) {
      setForcePasswordModalOpen(true);
      setPendingPasswordPrompt(false);
      setPendingContactPrompt(!controller?.userDetails?.isContactVerified || !controller?.userDetails?.contactNo);
      return;
    }
    if (pendingContactPrompt) {
      setContactModalOpen(true);
      setPendingContactPrompt(false);
    }
  };

  const handleForcePasswordChange = async () => {
    const passwordCheck = validatePassword(newPassword);
    if (!passwordCheck.isValid) {
      setPasswordError(passwordCheck.message);
      return;
    }
    const confirmCheck = validateConfirmPassword(newPassword, confirmPassword);
    if (!confirmCheck.isValid) {
      setPasswordError(confirmCheck.message);
      return;
    }
    if (!currentPassword) {
      setPasswordError("Current password is required");
      return;
    }

    setPasswordError("");
    try {
      setChangingPassword(true);
      const res = await post("/auth/resetPassword", {
        email: controller?.userDetails?.email,
        password: newPassword,
        currentPassword,
        session,
      });
      if (res?.status === 200) {
        sessionStorage.removeItem("forcePasswordChange");
        setForcePasswordModalOpen(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        if (!controller?.userDetails?.isContactVerified || !controller?.userDetails?.contactNo) {
          setContactModalOpen(true);
        }
        return;
      }
      setPasswordError(res?.message || "Unable to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const isValidContact = (value) => /^\d{8,15}$/.test(String(value || "").replace(/[\s-]/g, ""));
  const handleRequestContactOtp = async () => {
    const normalized = String(contactNo || "").replace(/[\s-]/g, "");
    if (!isValidContact(normalized)) {
      setContactError("Please enter a valid contact number.");
      return;
    }
    setContactError("");
    setContactInfo("");
    const res = await requestOTPAPI(post, { contactNo: normalized });
    if (res?.status === 200) {
      setOtpRequested(true);
      setContactInfo("OTP sent. Please enter the OTP to verify your contact number.");
      return;
    }
    setContactError(res?.message || "Unable to send OTP");
  };

  const handleVerifyContactOtp = async () => {
    if (!contactOtp.trim()) {
      setContactError("Please enter OTP.");
      return;
    }
    setContactError("");
    setContactInfo("");
    setVerifyingContactOtp(true);
    try {
      const res = await verifyOTPAPI(post, { otp: contactOtp });
      if (res?.status !== 200) {
        setContactError(res?.message || "Unable to verify OTP");
        return;
      }
      setContactModalOpen(false);
      setLogoutCountdown(5);
      setLogoutNoticeOpen(true);
    } finally {
      setVerifyingContactOtp(false);
    }
  };

  useEffect(() => {
    if (!logoutNoticeOpen) return undefined;

    if (logoutCountdown <= 0) {
      localStorage.clear();
      sessionStorage.clear();
      navigate("/auth/signin");
      return undefined;
    }

    const timer = setTimeout(() => {
      setLogoutCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [logoutNoticeOpen, logoutCountdown, navigate]);

  return (
    <MDBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        p: 3,
        position: "relative",

        [breakpoints.up("xl")]: {
          marginLeft: miniSidenav ? pxToRem(120) : pxToRem(274),
          transition: transitions.create(["margin-left", "margin-right"], {
            easing: transitions.easing.easeInOut,
            duration: transitions.duration.standard,
          }),
        },
      })}
    >
      <DashboardNavbar />
      {children}
      <AnnouncementModal open={announcementOpen} onClose={handleAnnouncementClose} />
      <Dialog open={forcePasswordModalOpen} disableEscapeKeyDown>
        <DialogTitle>Change Password Required</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" mb={2} display="block">
            For account security, please change your password before continuing.
          </MDTypography>

          <MDInput
            type={showCurrentPassword ? "text" : "password"}
            label="Current Password"
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrentPassword((s) => !s)} edge="end">
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <MDInput
            type={showNewPassword ? "text" : "password"}
            label="New Password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword((s) => !s)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <MDInput
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm New Password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword((s) => !s)} edge="end">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordError ? (
            <MDTypography variant="caption" color="error" mt={1} display="block">
              {passwordError}
            </MDTypography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <MDTypography variant="caption" color="info" fontWeight="medium" mb={2} display="block">
            {PASSWORD_RULE_TEXT}
          </MDTypography>
          <MDButton
            onClick={handleForcePasswordChange}
            variant="gradient"
            color="info"
            // fullWidth
            loading={changingPassword}
          >
            Update Password
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={contactModalOpen} disableEscapeKeyDown>
        <DialogTitle>Update & Verify Contact Number</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" mb={2} display="block">
            Please verify your contact number to continue.
          </MDTypography>
          <MDInput
            type="text"
            label="Contact Number"
            fullWidth
            value={contactNo}
            onChange={(e) => setContactNo(e.target.value)}
            sx={{ mb: 2 }}
          />
          {otpRequested ? (
            <MDInput
              type="text"
              label="OTP"
              fullWidth
              value={contactOtp}
              onChange={(e) => setContactOtp(e.target.value)}
            />
          ) : null}
          {contactError ? (
            <MDTypography variant="caption" color="error" mt={1} display="block">
              {contactError}
            </MDTypography>
          ) : null}
          {contactInfo ? (
            <MDTypography variant="caption" color="info" mt={1} display="block">
              {contactInfo}
            </MDTypography>
          ) : null}
        </DialogContent>
        <DialogActions>
          {!otpRequested ? (
            <MDButton onClick={handleRequestContactOtp} variant="gradient" color="info">
              Verify
            </MDButton>
          ) : (
            <MDButton
              onClick={handleVerifyContactOtp}
              variant="gradient"
              color="info"
              loading={verifyingContactOtp}
            >
              Verify OTP
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={logoutNoticeOpen} disableEscapeKeyDown>
        <DialogTitle>Contact Verified</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="success" mb={1} display="block">
            Your contact number is verified successfully.
          </MDTypography>
          <MDTypography variant="button" color="text" display="block">
            MFA is now enabled. You are about to be logged out in {logoutCountdown} seconds. Please
            login again to continue.
          </MDTypography>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
